// @claude-owned: TimerImmersive + LocalFab contexts — sprint P0 v3
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SmartFAB } from './SmartFAB';
import { Breadcrumbs } from './Breadcrumbs';
import { useSmartActions } from '@/hooks/useSmartActions';
import { useAuth } from '@/hooks/useAuth';
import { useAcademyContext } from '@/hooks/useAcademyContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useImpersonateStore } from '@/stores/useImpersonateStore';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { TimerImmersiveProvider, useTimerImmersive } from '@/contexts/TimerImmersiveContext';
import { LocalFabProvider, useHasLocalFab } from '@/contexts/LocalFabContext';

interface AppLayoutProps {
  children: ReactNode;
}

function AppLayoutInner({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentAcademy } = useAcademyContext();
  const { nextSession, pendingPaymentsCount, refresh } = useSmartActions();
  const { isImmersive } = useTimerImmersive();
  const hasLocalFab = useHasLocalFab();

  // Tactical Thai cockpit: forçar dark theme em todo o /app.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('dark');
    return () => root.classList.remove('dark');
  }, []);

  // FAB is suppressed whenever the timer is immersive or a page registered its own.
  const showFAB = Boolean(user) && !isImmersive && !hasLocalFab;

  const handleConfirmClass = async () => {
    if (!nextSession) return;
    if (!currentAcademy) {
      toast.error('Selecione uma academia primeiro');
      return;
    }

    // Get student phone (scoped to current academy)
    const { data: student } = await supabase
      .from('students')
      .select('phone')
      .eq('id', nextSession.studentId)
      .eq('academy_id', currentAcademy.id)
      .single();

    if (!student?.phone) {
      toast.error('Aluno sem WhatsApp cadastrado');
      return;
    }

    // Update schedule status (scoped to current academy)
    await supabase
      .from('schedule')
      .update({ status: 'confirmed' })
      .eq('id', nextSession.scheduleId)
      .eq('academy_id', currentAcademy.id);

    // Open WhatsApp
    const cleanPhone = student.phone.replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Olá ${nextSession.studentName.split(' ')[0]}! 👋 Confirmando seu treino de hoje às ${nextSession.time}. Tudo certo? 🥊`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank');
    
    toast.success('Confirmação enviada!');
    refresh();
  };

  const handleStartSession = () => {
    if (!nextSession) return;
    
    navigate('/app', {
      state: {
        studentId: nextSession.studentId,
        studentName: nextSession.studentName,
        scheduleId: nextSession.scheduleId,
      },
    });
  };

  const handleChargePayments = () => {
    navigate('/app/alunos');
    toast.info('Veja os alunos com pagamento atrasado');
  };

  const { isImpersonating, impersonatedUserName, stopImpersonation } = useImpersonateStore();

  return (
    <>
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-destructive text-destructive-foreground px-4 py-2 flex items-center justify-between text-sm font-medium shadow-lg">
          <span>⚠️ MODO CONCIERGE: Acessando como <strong>{impersonatedUserName}</strong></span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-destructive-foreground hover:bg-destructive-foreground/20 gap-1"
            onClick={() => {
              stopImpersonation();
              navigate('/admin');
            }}
          >
            <X className="h-3.5 w-3.5" />
            Sair
          </Button>
        </div>
      )}
      {!isImmersive && <Breadcrumbs />}
      {children}
      {showFAB && (
        <SmartFAB
          nextSession={nextSession}
          pendingPayments={pendingPaymentsCount}
          onConfirmClass={handleConfirmClass}
          onStartSession={handleStartSession}
          onChargePayments={handleChargePayments}
        />
      )}
    </>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <TimerImmersiveProvider>
      <LocalFabProvider>
        <AppLayoutInner>{children}</AppLayoutInner>
      </LocalFabProvider>
    </TimerImmersiveProvider>
  );
}